import { parse as parseYaml } from 'yaml';
import { z } from 'zod';

/**
 * Addon manifest schema — mirrors the YAML shape documented in
 * specs/SAZEO_V2_ANATOMY.md §7.1.
 *
 * The manifest is the source of truth for how an addon plugs into the host
 * shell: menu entries, dashboard widgets, entity tabs, inline actions, API
 * surface, migrations, translations, and Claude tools. The runtime
 * (registry + mount) consumes this schema; the build pipeline validates
 * every `manifest.yaml` against it before packaging.
 */

/** Package versions are tightly scoped — permissive semver only, no build metadata. */
const VersionSchema = z
  .string()
  .regex(
    /^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/,
    'version must be semver (MAJOR.MINOR.PATCH, optional -prerelease)',
  );

/** DNS-label-style addon id: lowercase, starts with a letter, hyphens allowed. */
const AddonIdSchema = z
  .string()
  .regex(
    /^[a-z][a-z0-9-]{1,62}$/,
    'id must start with a lowercase letter and be 2-63 chars of [a-z0-9-]',
  );

const CategorySchema = z.enum([
  'hr',
  'sales',
  'finance',
  'operations',
  'intelligence',
  'communication',
  'integrations',
]);

const VendorSchema = z.enum(['sazeo', 'community', 'tenant_local']);

const AuthoringSchema = z.enum(['human', 'ai_generated', 'hybrid']);

const PricingSchema = z.object({
  model: z.enum(['flat', 'per_seat', 'metered']),
  price_usd: z.number().nonnegative(),
  unit: z.enum(['month', 'year', 'event']),
  trial_days: z.number().int().nonnegative(),
});

const PermissionSchema = z.object({
  scope: z.string().min(1),
});

const MenuEntrySchema = z.object({
  icon: z.string().min(1),
  route: z.string().startsWith('/'),
  label_key: z.string().min(1),
  order: z.number().int(),
  requires_role: z.array(z.string().min(1)).optional(),
});

const DashboardWidgetSchema = z.object({
  id: z.string().min(1),
  component: z.string().min(1),
  default_size: z.enum(['small', 'medium', 'large']),
  default_enabled: z.boolean(),
});

const EntityTabSchema = z.object({
  entity: z.string().min(1),
  tab_key: z.string().min(1),
  component: z.string().min(1),
});

const InlineActionSchema = z.object({
  entity: z.string().min(1),
  action: z.string().min(1),
  label_key: z.string().min(1),
  handler: z.string().min(1),
});

const ExtensionPointsSchema = z.object({
  menu_entry: MenuEntrySchema.optional(),
  dashboard_widgets: z.array(DashboardWidgetSchema).optional(),
  entity_tabs: z.array(EntityTabSchema).optional(),
  inline_actions: z.array(InlineActionSchema).optional(),
});

const ApiEndpointSchema = z.object({
  method: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']),
  path: z.string().startsWith('/'),
  handler: z.string().min(1),
  scopes: z.array(z.string().min(1)),
});

const ApiSchema = z.object({
  endpoints: z.array(ApiEndpointSchema).optional(),
  webhooks: z.array(z.string().min(1)).optional(),
});

const TableSchema = z.object({
  name: z.string().min(1),
  migration: z.string().min(1),
});

const TranslationsSchema = z.object({
  i18n_namespace: z.string().min(1),
  source_lang: z.string().min(2),
});

const ClaudeToolManifestSchema = z.object({
  name: z.string().min(1),
  description: z.string().min(1),
  read_scope: z.string().optional(),
  write_scope: z.string().optional(),
  approval_required: z.boolean().optional(),
});

export const AddonManifestSchema = z.object({
  schema_version: z.literal(1),
  id: AddonIdSchema,
  name: z.string().min(1),
  description: z.string().min(1),
  category: CategorySchema,
  version: VersionSchema,
  vendor: VendorSchema,
  authoring: AuthoringSchema,
  pricing: PricingSchema,
  permissions: z.array(PermissionSchema),
  extension_points: ExtensionPointsSchema,
  api: ApiSchema.optional(),
  tables: z.array(TableSchema).optional(),
  translations: TranslationsSchema.optional(),
  claude_tools: z.array(ClaudeToolManifestSchema).optional(),
});

export type AddonManifest = z.infer<typeof AddonManifestSchema>;
export type AddonMenuEntry = z.infer<typeof MenuEntrySchema>;
export type AddonDashboardWidget = z.infer<typeof DashboardWidgetSchema>;
export type AddonEntityTab = z.infer<typeof EntityTabSchema>;
export type AddonInlineAction = z.infer<typeof InlineActionSchema>;
export type AddonClaudeToolRef = z.infer<typeof ClaudeToolManifestSchema>;

/**
 * Parse an already-deserialised manifest object (whatever shape the caller
 * got from a yaml/json parser). Returns the parsed manifest on success or
 * the zod error on failure — callers decide how to surface errors (CI fails
 * loudly; runtime skips the addon and emits an audit event).
 */
export function parseAddonManifest(raw: unknown): AddonManifest | z.ZodError {
  const result = AddonManifestSchema.safeParse(raw);
  if (result.success) return result.data;
  return result.error;
}

/**
 * Parse a raw YAML string straight to a validated `AddonManifest`. Throws
 * on invalid YAML or schema violation — useful in build scripts / tests
 * where a malformed manifest is a hard failure.
 */
export function parseAddonManifestYaml(yaml: string): AddonManifest {
  const doc = parseYaml(yaml) as unknown;
  const result = parseAddonManifest(doc);
  if (result instanceof z.ZodError) {
    throw new Error(`Invalid addon manifest: ${JSON.stringify(result.issues, null, 2)}`);
  }
  return result;
}

/**
 * Throws on invalid manifest — convenience wrapper for call sites where a
 * thrown error is the right flow-control signal (build-time validation).
 */
export function validateAddonManifest(raw: unknown): AddonManifest {
  const result = parseAddonManifest(raw);
  if (result instanceof z.ZodError) {
    throw new Error(`Invalid addon manifest: ${JSON.stringify(result.issues, null, 2)}`);
  }
  return result;
}
